// Miky Motorsport - Three.js scena, renderer, svetla, gradient obloha + envmap, auto
import * as THREE from 'three';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { PostFX } from './post.js';
import { loftGeometry } from '../loftGeometry.js';

const SUN_ELEVATION = 30;
const SUN_AZIMUTH = 135;
const BODY_Y = -0.90; // posun vizualni karoserie dolu (chassis origin ~1m nad zemi -> telo k zemi/kolum)

export class SceneManager {
  constructor(canvas) {
    this.canvas = canvas;

    // preserveDrawingBuffer: aby slo zachytit snimek (foto mod)
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance', preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping; // fallback; po setupPost -> NoToneMapping
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.info.autoReset = false;

    this.scene = new THREE.Scene();
    this.scene.environmentIntensity = 0.9;

    const phi = THREE.MathUtils.degToRad(90 - SUN_ELEVATION);
    const theta = THREE.MathUtils.degToRad(SUN_AZIMUTH);
    this._sunDir = new THREE.Vector3().setFromSphericalCoords(1, phi, theta);

    // --- Gradient obloha (teply zapad/golden hour - stylizovane, kohezni s low-poly) ---
    const topCol = new THREE.Color(0x2c4488);
    const botCol = new THREE.Color(0xf6b276);
    this.sky = makeGradientSky(topCol, botCol, 900); // < camera far (1400)
    this.sky.renderOrder = -1000;
    this.scene.add(this.sky);

    // --- Envmap z oblohy (fallback nez se nacte HDRI) ---
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const envScene = new THREE.Scene();
    envScene.add(makeGradientSky(topCol, botCol, 800));
    this.scene.environment = pmrem.fromScene(envScene, 0.0).texture;
    pmrem.dispose();

    this.scene.fog = new THREE.Fog(0xe6c89e, 240, 760); // tepla zapadova mlha

    // --- Svetla (golden hour) ---
    this.sun = new THREE.DirectionalLight(0xffdca6, 2.6);
    this.sun.position.copy(this._sunDir).multiplyScalar(120);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 260;
    const sc = 55;
    this.sun.shadow.camera.left = -sc;
    this.sun.shadow.camera.right = sc;
    this.sun.shadow.camera.top = sc;
    this.sun.shadow.camera.bottom = -sc;
    this.sun.shadow.bias = -0.0004;
    this.sun.shadow.normalBias = 0.025;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    this.hemi = new THREE.HemisphereLight(0xd8c0a0, 0x53504a, 0.6);
    this.scene.add(this.hemi);

    this.post = null;
  }

  // HDRI prostredi (CC0) -> realisticke reflexe na laku/sklech + image-based lighting.
  // background=true pouzije HDRI i jako oblohu (skryje gradient dome).
  async loadHDREnvironment(url, { background = true } = {}) {
    return new Promise((resolve) => {
      new RGBELoader().load(url, (hdr) => {
        hdr.mapping = THREE.EquirectangularReflectionMapping;
        const pmrem = new THREE.PMREMGenerator(this.renderer);
        pmrem.compileEquirectangularShader();
        const env = pmrem.fromEquirectangular(hdr).texture;
        if (this.scene.environment) this.scene.environment.dispose(); // uvolni predchozi (gradient) env
        this.scene.environment = env;
        this.scene.environmentIntensity = 0.9;
        this._biomeKey = 'meadow'; // HDRI = meadow look; switchTrack na meadow nechá HDRI
        if (background) {
          this.scene.background = hdr; // equirekt obloha
          this.scene.backgroundBlurriness = 0.0;
          if (this.sky) this.sky.visible = false; // skryj gradient dome
          // posun mlhu k teplym tonum zapadu
          if (this.scene.fog) this.scene.fog.color.set(0xd8c0a0);
        }
        pmrem.dispose();
        if (!background) hdr.dispose(); // raw HDR uz nepotreba (neni pozadi)
        resolve();
      }, undefined, (err) => { console.warn('HDRI load fail', err); resolve(); });
    });
  }

  setupPost(camera) {
    this.post = new PostFX(this.renderer, this.scene, camera);
    this.renderer.toneMapping = THREE.NoToneMapping; // tonemapping resi post (NEUTRAL)
    this.post.setSize(window.innerWidth, window.innerHeight);
  }

  // Aplikuje biom (obloha/slunce/mlha/reflexe) - vola se pri prepnuti trati.
  applyBiome(b) {
    if (!b) return;
    this._biome = b;
    const top = new THREE.Color(b.skyTop), bot = new THREE.Color(b.skyBot);
    // gradient obloha (dome) + skryj HDRI pozadi
    this.scene.background = null;
    if (this.sky) {
      this.sky.visible = true;
      this.sky.material.uniforms.topColor.value.copy(top);
      this.sky.material.uniforms.bottomColor.value.copy(bot);
    }
    // envmap z gradientu (reflexe ladi s biomem)
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    const es = new THREE.Scene();
    es.add(makeGradientSky(top, bot, 800));
    if (this.scene.environment) this.scene.environment.dispose();
    this.scene.environment = pmrem.fromScene(es, 0.0).texture;
    this.scene.environmentIntensity = b.env ?? 0.9;
    pmrem.dispose();
    es.traverse((o) => { o.geometry?.dispose?.(); if (o.material) o.material.dispose(); }); // uvolni docasny gradient mesh
    // slunce
    const phi = THREE.MathUtils.degToRad(90 - b.sunEl), theta = THREE.MathUtils.degToRad(b.sunAz);
    this._sunDir.setFromSphericalCoords(1, phi, theta);
    this.sun.color.set(b.sun); this.sun.intensity = b.sunInt;
    this.sun.position.copy(this._sunDir).multiplyScalar(120);
    // hemisfericke svetlo
    this.hemi.color.set(b.hemiSky); this.hemi.groundColor.set(b.hemiGround); this.hemi.intensity = b.hemiInt;
    // mlha
    if (this.scene.fog) { this.scene.fog.color.set(b.fog); this.scene.fog.near = b.fogNear; this.scene.fog.far = b.fogFar; }
  }

  add(obj) {
    this.scene.add(obj);
  }
  remove(obj) {
    this.scene.remove(obj);
  }

  updateSun(targetPos) {
    this.sun.target.position.copy(targetPos);
    this.sun.position.copy(targetPos).addScaledVector(this._sunDir, 120);
  }

  resize(w, h) {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    this.renderer.setSize(w, h, false);
    if (this.post) this.post.setSize(w, h);
  }

  render(camera, dt) {
    this.renderer.info.reset();
    if (this.post) this.post.render(dt);
    else this.renderer.render(this.scene, camera);
  }

  // A5: sila vignety rizena rychlosti (no-op kdyz post vypnuty / nizky tier).
  setSpeedVignette(darkness) {
    if (this.post) this.post.setVignette(darkness);
  }

  get drawCalls() {
    return this.renderer.info.render.calls;
  }
  get triangles() {
    return this.renderer.info.render.triangles;
  }

  // Postavi auto: hero vizual je PROCEDURALNI (lofted karoserie). modelKey slouzi
  // jako "varianta" siluety (ruzny tvar per auto/AI), aby bylo startovni pole pestre.
  buildCar(spec, modelKey) {
    const variant = VARIANT_BY_KEY[modelKey || spec.model] || null;
    return this._buildProceduralCar(spec, variant);
  }

  // --- Proceduralni HERO auto: lofted hladka karoserie + sklenena kabina + interier +
  //     PBR materialy + kola. Silueta parametricky per varianta (ruzny tvar per auto). ---
  _buildProceduralCar(spec, variant) {
    const sil = SILHOUETTES[variant] || SILHOUETTES[spec.cls] || SILHOUETTES.C;
    const group = new THREE.Group();
    const bodyGroup = new THREE.Group();
    bodyGroup.position.y = BODY_Y;
    group.add(bodyGroup);

    const hw = (spec.chassis.hx + 0.06) * sil.widthScale; // polosirka karoserie
    const hl = (spec.chassis.hz + 0.30) * sil.lenScale;   // polodelka
    const sh = sil.shoulder;   // ramenni linie (kde zacina sklo)
    const roof = sil.roof;     // vyska strechy
    const wedge = sil.wedge;   // klin (predek niz nez zadek)
    const zAt = (f) => (0.5 - f) * 2 * hl; // f: 0 = predek (+z), 1 = zadek (-z)

    // --- Materialy (PBR) ---
    const paintMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(spec.color), metalness: 0.0, roughness: 0.36,
      clearcoat: 0.7, clearcoatRoughness: 0.18, envMapIntensity: 0.9,
    });
    // sklo: reflexivni tonovane (cte se jako sklo diky odrazum HDRI), strednie pruhledne
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0x7d93a6, metalness: 0.0, roughness: 0.04, clearcoat: 1.0, clearcoatRoughness: 0.04,
      envMapIntensity: 2.6, transparent: true, opacity: 0.52, side: THREE.DoubleSide,
    });
    const trimMat = new THREE.MeshStandardMaterial({ color: 0x0b0c10, metalness: 0.45, roughness: 0.5 });
    const interiorMat = new THREE.MeshStandardMaterial({ color: 0x1a1b20, roughness: 0.9 });
    const chromeMat = new THREE.MeshStandardMaterial({ color: 0xc6cad0, metalness: 1.0, roughness: 0.26, envMapIntensity: 1.3 });
    const headMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xfff2d4, emissiveIntensity: 1.4 });
    const brakeMat = new THREE.MeshStandardMaterial({ color: 0x4e0805, emissive: 0xff1a0a, emissiveIntensity: 0.85 });

    // --- Lofted spodni karoserie (hladka, siroke nizke blatniky) ---
    const yb = 0.02; // spodni (blatnikova) linie karoserie
    const bodyStations = [
      { f: 0.00, w: 0.52, yl: yb + 0.10 + wedge, yh: 0.38 + wedge, box: 2.3 },
      { f: 0.10, w: 0.90, yl: yb, yh: sh - 0.14 + wedge * 0.4, box: 3.0 },
      { f: 0.22, w: 1.00, yl: yb, yh: sh - 0.04, box: 3.4 },
      { f: 0.40, w: 1.00, yl: yb, yh: sh, box: 3.6 },
      { f: 0.52, w: 1.00, yl: yb, yh: sh, box: 3.6 },
      { f: 0.66, w: 1.00, yl: yb, yh: sh - 0.01, box: 3.5 },
      { f: 0.80, w: 0.97, yl: yb + 0.02, yh: sh - 0.08, box: 3.2 },
      { f: 0.90, w: 0.88, yl: yb + 0.06, yh: sh - 0.16, box: 2.8 },
      { f: 1.00, w: 0.58, yl: yb + 0.12, yh: sh - 0.22, box: 2.3 },
    ];
    const bodyMesh = new THREE.Mesh(loft(bodyStations, hw, hl), paintMat);
    bodyMesh.castShadow = true; bodyMesh.receiveShadow = true;
    bodyGroup.add(bodyMesh);

    // --- Kabina: sklenny pas oken + PLOCHA strecha v barve karoserie (ne sklenena bublina) ---
    const winTop = sh + 0.18;                  // horni hrana oken / patka strechy
    const rp = winTop + 0.07;                  // vrchol strechy (jen mirne nad okny = plocha strecha)
    const cyl = sh - 0.07;                      // patka oken zapustena do tela (bez sterbiny)
    // sklenny pas (cela/bocni/zadni okna) - pres celou kabinu, mirna tumblehome
    const glassStations = [
      { f: 0.40, w: 0.82, yl: cyl, yh: winTop - 0.04, box: 4.4 }, // patka celniho skla (sklon)
      { f: 0.49, w: 0.88, yl: cyl, yh: winTop, box: 5.0 },
      { f: 0.62, w: 0.90, yl: cyl, yh: winTop, box: 5.2 },        // bocni okna
      { f: 0.74, w: 0.88, yl: cyl, yh: winTop, box: 5.0 },
      { f: 0.85, w: 0.76, yl: cyl, yh: winTop - 0.05, box: 4.4 }, // patka zadniho skla (sklon)
    ];
    const glassMesh = new THREE.Mesh(loft(glassStations, hw, hl), glassMat);
    glassMesh.castShadow = true;
    bodyGroup.add(glassMesh);
    // pevna strecha (barva karoserie) - plocha, sedi na pasu oken, sirka jako okna (flush)
    const roofStations = [
      { f: 0.46, w: 0.84, yl: winTop - 0.04, yh: rp - 0.01, box: 5.2 }, // za celnim sklem
      { f: 0.55, w: 0.90, yl: winTop - 0.04, yh: rp, box: 6.0 },        // plochy vrchol
      { f: 0.67, w: 0.90, yl: winTop - 0.04, yh: rp, box: 6.0 },
      { f: 0.77, w: 0.82, yl: winTop - 0.04, yh: rp - 0.02, box: 5.2 }, // pred zadnim sklem
    ];
    const roofMesh = new THREE.Mesh(loft(roofStations, hw, hl), paintMat);
    roofMesh.castShadow = true;
    bodyGroup.add(roofMesh);

    // --- Interier (kokpit, ridic VLEVO = -x): tmava vana + palubka + sedacka + volant + hlava ---
    const DX = -0.30; // ridic na leve strane (-x = levá; FL kolo je na -x)
    const tub = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.4, 0.14, hl * 0.55), interiorMat);
    tub.position.set(0, sh - 0.05, zAt(0.6)); bodyGroup.add(tub);
    // palubka pres sirku, pod celnim sklem
    const dash = new THREE.Mesh(new THREE.BoxGeometry(hw * 1.3, 0.1, 0.22), interiorMat);
    dash.position.set(0, sh + 0.02, zAt(0.48)); bodyGroup.add(dash);
    // sedacka (operadlo) ridice
    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.3, 0.12), interiorMat);
    seat.position.set(DX, sh + 0.08, zAt(0.69)); bodyGroup.add(seat);
    // volant (vlevo)
    const swheel = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.022, 8, 16), trimMat);
    swheel.rotation.x = Math.PI * 0.36; swheel.position.set(DX, sh + 0.04, zAt(0.55)); bodyGroup.add(swheel);
    // hlava ridice (vlevo)
    const driver = new THREE.Mesh(new THREE.SphereGeometry(0.092, 12, 10),
      new THREE.MeshStandardMaterial({ color: 0xf0c79e, roughness: 0.7 }));
    driver.position.set(DX, sh + 0.15, zAt(0.63)); bodyGroup.add(driver);

    // Helper na boxy (v bodyGroup)
    const addBox = (sx, sy, sz, px, py, pz, mat, shadow = true) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(sx, sy, sz), mat);
      m.position.set(px, py, pz); m.castShadow = shadow; bodyGroup.add(m); return m;
    };

    // Predni splitter, zadni difuzor, prahy (decentni, nevycnivaji)
    addBox(hw * 1.55, 0.05, 0.16, 0, 0.08, zAt(0.05), trimMat);
    addBox(hw * 1.5, 0.08, 0.16, 0, 0.09, zAt(0.96), trimMat);
    for (const sx of [-1, 1]) addBox(0.05, 0.09, hl * 1.05, sx * hw * 1.0, 0.10, zAt(0.52), trimMat);

    // Predni svetla (na masce/blatniku)
    for (const sx of [-1, 1]) addBox(0.30, 0.09, 0.08, sx * hw * 0.58, sh - 0.22, zAt(0.085), headMat, false);
    // Zadni svetelny pruh (brzdove) - na zadnim panelu
    addBox(hw * 1.25, 0.09, 0.05, 0, sh - 0.20, zAt(0.92), brakeMat, false);

    // Zpetna zrcatka (tesne u tela, na predu bocniho okna)
    for (const sx of [-1, 1]) {
      addBox(0.07, 0.025, 0.04, sx * (hw + 0.02), sh + 0.04, zAt(0.47), trimMat);  // kratke raminko
      addBox(0.04, 0.06, 0.1, sx * (hw + 0.07), sh + 0.05, zAt(0.47), chromeMat);  // male zrcatko
    }

    // Kridlo + vyfuky (sportovni varianty)
    if (sil.wing) {
      addBox(hw * 1.7, 0.04, 0.3, 0, sh + 0.2, zAt(0.93), trimMat);
      for (const sx of [-1, 1]) addBox(0.05, 0.16, 0.12, sx * hw * 0.66, sh + 0.12, zAt(0.93), trimMat);
      for (const sx of [-1, 1]) {
        const ex = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 0.14, 12), chromeMat);
        ex.rotation.x = Math.PI / 2; ex.position.set(sx * 0.3, 0.12, zAt(0.995)); bodyGroup.add(ex);
      }
    }

    // --- Kola (pneu + brzdovy kotouc + disk se spicemi) ---
    const wheels = [];
    const tireMat = new THREE.MeshStandardMaterial({ color: 0x141519, roughness: 0.86 });
    const rimMat = new THREE.MeshStandardMaterial({ color: 0xd6dae2, metalness: 0.95, roughness: 0.2, envMapIntensity: 1.3 });
    const discMat = new THREE.MeshStandardMaterial({ color: 0x55585f, metalness: 0.7, roughness: 0.4 });
    const R = spec.wheel.radius, WW = spec.wheel.width;
    for (let i = 0; i < spec.wheel.positions.length; i++) {
      const wg = new THREE.Group();
      const tireGeo = new THREE.CylinderGeometry(R, R, WW, 24); tireGeo.rotateZ(Math.PI / 2);
      const tire = new THREE.Mesh(tireGeo, tireMat); tire.castShadow = true; wg.add(tire);
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.58, R * 0.58, WW * 0.5, 18), discMat);
      disc.rotateZ(Math.PI / 2); wg.add(disc);
      const rimGeo = new THREE.CylinderGeometry(R * 0.62, R * 0.62, WW * 1.04, 16); rimGeo.rotateZ(Math.PI / 2);
      wg.add(new THREE.Mesh(rimGeo, rimMat));
      for (let s = 0; s < 5; s++) {
        const spoke = new THREE.Mesh(new THREE.BoxGeometry(WW * 1.06, R * 1.08, 0.05), rimMat);
        spoke.rotation.x = (s / 5) * Math.PI * 2; wg.add(spoke);
      }
      group.add(wg); wheels.push(wg);
    }

    // Blatnikove oblouky (tmavy lip nad koly) - ramuji kola a maskuji prechod karoserie/kolo
    for (const p of spec.wheel.positions) {
      const arch = new THREE.Mesh(new THREE.TorusGeometry(R * 1.08, 0.045, 6, 18, Math.PI), trimMat);
      arch.rotation.y = Math.PI / 2;          // oblouk v rovine kola (podel auta)
      arch.position.set(p.x, p.y - 0.40, p.z); // nominalni stred kola
      arch.castShadow = true;
      group.add(arch);
    }

    group.userData.wheels = wheels;
    return { group, wheels, brakeMat };
  }

  disposeObject(obj) {
    obj.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        for (const m of mats) {
          for (const k in m) {
            const v = m[k];
            if (v && v.isTexture) v.dispose();
          }
          m.dispose();
        }
      }
    });
  }

  dispose() {
    if (this.post) this.post.dispose();
    this.scene.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => m.dispose());
      }
    });
    if (this.scene.environment) this.scene.environment.dispose();
    this.renderer.dispose();
  }
}

// Gradient obloha (dome, BackSide) - kontrolovany jas
function makeGradientSky(top, bottom, radius) {
  const geo = new THREE.SphereGeometry(radius, 32, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      topColor: { value: top.clone() },
      bottomColor: { value: bottom.clone() },
      offset: { value: 0.12 },
      power: { value: 0.65 },
    },
    vertexShader: `
      varying vec3 vDir;
      void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
    `,
    fragmentShader: `
      uniform vec3 topColor; uniform vec3 bottomColor; uniform float offset; uniform float power;
      varying vec3 vDir;
      void main(){
        float h = clamp(vDir.y + offset, 0.0, 1.0);
        gl_FragColor = vec4(mix(bottomColor, topColor, pow(h, power)), 1.0);
      }
    `,
  });
  return new THREE.Mesh(geo, mat);
}

// --- Silueta auta per varianta (proporce karoserie). shoulder=ramenni linie,
//     roof=vyska strechy, wedge=klin (predek niz), widthScale/lenScale=meritko, wing=kridlo. ---
const SILHOUETTES = {
  compact:  { shoulder: 0.64, roof: 1.02, wedge: 0.04, widthScale: 0.98, lenScale: 0.94, wing: false },
  sedan:    { shoulder: 0.62, roof: 1.00, wedge: 0.05, widthScale: 1.00, lenScale: 1.02, wing: false },
  wagon:    { shoulder: 0.64, roof: 1.05, wedge: 0.04, widthScale: 1.00, lenScale: 1.05, wing: false },
  suv:      { shoulder: 0.72, roof: 1.12, wedge: 0.02, widthScale: 1.02, lenScale: 1.0,  wing: false },
  coupe:    { shoulder: 0.58, roof: 0.92, wedge: 0.10, widthScale: 1.03, lenScale: 1.0,  wing: true },
  roadster: { shoulder: 0.56, roof: 0.84, wedge: 0.11, widthScale: 1.02, lenScale: 0.98, wing: false },
  hyper:    { shoulder: 0.54, roof: 0.86, wedge: 0.15, widthScale: 1.05, lenScale: 1.0,  wing: true },
  // fallback dle tridy (kdyz neni varianta)
  D: { shoulder: 0.64, roof: 1.02, wedge: 0.04, widthScale: 0.98, lenScale: 0.96, wing: false },
  C: { shoulder: 0.62, roof: 1.00, wedge: 0.05, widthScale: 1.00, lenScale: 1.0,  wing: false },
  B: { shoulder: 0.58, roof: 0.92, wedge: 0.10, widthScale: 1.03, lenScale: 1.0,  wing: true },
  S: { shoulder: 0.54, roof: 0.86, wedge: 0.15, widthScale: 1.05, lenScale: 1.0,  wing: true },
};

// model-key (drive z dat aut) -> varianta siluety. Hraci auta (q1/q2/q6/q5) maji tvar
// odpovidajici sve tride; ostatni klice davaji rozmanitost AI poli.
const VARIANT_BY_KEY = {
  q1: 'compact', q2: 'sedan', q3: 'wagon', q4: 'suv', q5: 'hyper', q6: 'coupe', q7: 'roadster',
};

// Lofted uzavreny hull z podelnych "stations" (cross-section ringy spojene do plochy).
// hw = polosirka, hl = polodelka. station: { f:0..1 (predek->zadek), w:frakce sirky, yl, yh, box }.
// Cista aritmetika (positions+indices) je v loftGeometry; zde se z ni postavi BufferGeometry.
function loft(stations, hw, hl) {
  const { positions, indices } = loftGeometry(stations, hw, hl);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}
